// ============================================================================
// EXECUTIVE ASSESSMENT RENDERER  v5
// Unified design system. CSS namespace: .ea-*
// Schema fields: executive_summary, overall_health, headline_signals,
//   time_profile, major_discovery_rules, path_insights, leadership_priorities,
//   consultant_note, data_coverage
// ============================================================================

const esc = (v) => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
const fmt = (t='') => esc(t).replace(/\*\*([^*]+)\*\*/g,'<strong>$1</strong>').replace(/\n/g,'<br/>');
const money = (n, currency='AUD') => (n == null || !Number.isFinite(Number(n))) ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(n));

const HEALTH_META = {
  Excellent:      { bg:'#E6F1FB',color:'#0052CC',ring:'#0C66E4',bar:'#0C66E4' },
  Good:           { bg:'#DEEBFF',color:'#0747A6',ring:'#0052CC',bar:'#0052CC' },
  'Needs Attention':{ bg:'#FFF0B3',color:'#974F0C',ring:'#FF8B00',bar:'#FF8B00' },
  Poor:           { bg:'#FFEBE6',color:'#BF2600',ring:'#DE350B',bar:'#DE350B' },
  Critical:       { bg:'#FFEBE6',color:'#BF2600',ring:'#BF2600',bar:'#BF2600' },
};
const getHealth = (r) => HEALTH_META[r] || HEALTH_META['Needs Attention'];

const CONFIDENCE = {
  High:    { bg:'#E6F1FB',color:'#0052CC',icon:'✓' },
  Moderate:{ bg:'#DEEBFF',color:'#0747A6',icon:'~' },
  Low:     { bg:'#FFF0B3',color:'#974F0C',icon:'?' },
};
const getConf = (c) => CONFIDENCE[c] || CONFIDENCE.Moderate;

const SOURCE_MAP = {
  process_summary:{label:'Process',color:'#0052CC'},
  transitions:{label:'Transitions',color:'#FF8B00'},
  transition_signals:{label:'Transitions',color:'#FF8B00'},
  variants:{label:'Variants',color:'#0C66E4'},
  variant_signals:{label:'Variants',color:'#0C66E4'},
  workload_signals:{label:'Attributes',color:'#FF8B00'},
  status_model:{label:'Status',color:'#6B778C'},
  time_intelligence:{label:'Time Intel',color:'#00B8D9'},
  time_intel:{label:'Time Intel',color:'#00B8D9'},
};
const srcBadge = (src) => {
  const k=(src||'').toLowerCase().replace(/\s+/g,'_');
  const m=SOURCE_MAP[k]||{label:src||'Data',color:'#6B778C'};
  return `<span style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:2px 8px;border-radius:999px;background:${m.color}18;color:${m.color};border:1px solid ${m.color}35;white-space:nowrap;">${esc(m.label)}</span>`;
};


const numberFmt = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n));
};

const renderRoiSummary = (roi, roiEnabled) => {
  if (!roiEnabled || !roi) return '';
  const currency = roi?.assumptions?.currency || 'AUD';
    return `<div class="ea-section">${secHdr('roi','Estimated Cost of Inefficiency','#0C66E4')}
    <div style="display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;">
      <div style="border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:14px 16px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:8px;">Administrative Delay</div><div style="font-size:24px;font-weight:800;color:#172B4D;">${numberFmt(roi.delay_cost)}</div></div>
      <div style="border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:14px 16px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:8px;">Rework</div><div style="font-size:24px;font-weight:800;color:#172B4D;">${numberFmt(roi.rework_cost)}</div></div>
      <div style="border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:14px 16px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:8px;">Process Complexity</div><div style="font-size:24px;font-weight:800;color:#172B4D;">${numberFmt(roi.complexity_cost)}</div></div>
      <div style="border:1px solid #B3D4FF;border-radius:10px;background:linear-gradient(135deg,#F0F7FF 0%,#DEEBFF 100%);padding:14px 16px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:8px;">Total Monthly Impact</div><div style="font-size:24px;font-weight:800;color:#0747A6;">${numberFmt(roi.total_cost)}</div></div>
    </div>
    <div style="margin-top:14px;font-size:13px;line-height:1.7;color:#42526E;">Estimated savings potential: <strong>${money(roi.savings_low, currency)} – ${money(roi.savings_high, currency)} / month</strong>. Calculated using an hourly rate of <strong>${money(roi?.assumptions?.hourly_rate, currency)}</strong>.</div>
  </div>`;
};

const renderRoiExplainer = (roi, roiEnabled) => {
  if (!roiEnabled || !roi) return '';
  const currency = roi?.assumptions?.currency || 'AUD';
    return `<div class="ea-section">${secHdr('roi-explainer','How ROI Works','#5E6C84')}
    <div style="border:1px solid #DFE1E6;border-radius:10px;background:#FAFBFC;padding:14px 16px;">
      <div style="font-size:13px;line-height:1.7;color:#42526E;margin-bottom:10px;">In Executive Assessment, ROI provides a high-level estimate of the cost of delay, rework, and workflow complexity across the current process.</div>
      <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-bottom:10px;">
        <div style="border:1px solid #EBECF0;border-radius:8px;background:#fff;padding:10px 12px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:6px;">Administrative Delay</div><div style="font-size:13px;color:#172B4D;line-height:1.6;">Volume × admin / queue hours × hourly rate</div></div>
        <div style="border:1px solid #EBECF0;border-radius:8px;background:#fff;padding:10px 12px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:6px;">Rework</div><div style="font-size:13px;color:#172B4D;line-height:1.6;">Volume × rework rate × touch-time proxy × hourly rate</div></div>
        <div style="border:1px solid #EBECF0;border-radius:8px;background:#fff;padding:10px 12px;"><div style="font-size:11px;font-weight:700;color:#6B778C;text-transform:uppercase;margin-bottom:6px;">Complexity</div><div style="font-size:13px;color:#172B4D;line-height:1.6;">Volume × touch-time proxy × complexity overhead</div></div>
      </div>
      <div style="font-size:13px;line-height:1.7;color:#42526E;">Current assumption: <strong>${money(roi?.assumptions?.hourly_rate, currency)} per hour</strong>. Turn ROI off when cost framing does not make sense for the audience.</div>
    </div>
  </div>`;
};

const SIGNAL_STATUS_MAP = {
  excellent: { cls: 'excellent', label: 'Excellent' },
  very_good: { cls: 'excellent', label: 'Excellent' },
  strength: { cls: 'excellent', label: 'Excellent' },
  good: { cls: 'good', label: 'Good' },
  positive: { cls: 'good', label: 'Good' },
  ok: { cls: 'good', label: 'Good' },
  healthy: { cls: 'good', label: 'Good' },
  neutral: { cls: 'neutral', label: 'Neutral' },
  info: { cls: 'neutral', label: 'Neutral' },
  informational: { cls: 'neutral', label: 'Neutral' },
  observation: { cls: 'neutral', label: 'Neutral' },
  warning: { cls: 'warning', label: 'Warning' },
  caution: { cls: 'warning', label: 'Warning' },
  concern: { cls: 'warning', label: 'Warning' },
  concerning: { cls: 'warning', label: 'Warning' },
  attention: { cls: 'warning', label: 'Warning' },
  critical: { cls: 'critical', label: 'Critical' },
  danger: { cls: 'critical', label: 'Critical' },
  severe: { cls: 'critical', label: 'Critical' },
  poor: { cls: 'critical', label: 'Critical' },
};

const normaliseSignalStatus = (signal = {}) => {
  const raw = String(
    signal.signal_status ||
    signal.status_colour ||
    signal.status_color ||
    signal.status ||
    signal.tone ||
    signal.severity ||
    ''
  ).trim().toLowerCase().replace(/[\s-]+/g, '_');

  return SIGNAL_STATUS_MAP[raw] || SIGNAL_STATUS_MAP.neutral;
};


const DISCOVERY_RULE_TYPE = {
  'Critical Issue':   { icon:'🔴', color:'#BF2600', bg:'#FFEBE6', accent:'#DE350B' },
  'Major Issue':      { icon:'🟠', color:'#974F0C', bg:'#FFF0B3', accent:'#FF8B00' },
  'Opportunity':      { icon:'🔵', color:'#0747A6', bg:'#DEEBFF', accent:'#0052CC' },
  'Risk':             { icon:'⚠️', color:'#974F0C', bg:'#FFF0B3', accent:'#FF8B00' },
  'Strength':         { icon:'✅', color:'#0052CC', bg:'#E6F1FB', accent:'#0C66E4' },
  'Observation':      { icon:'💡', color:'#505F79', bg:'#F4F5F7', accent:'#6B778C' },
};
const getFT = (t) => DISCOVERY_RULE_TYPE[t] || DISCOVERY_RULE_TYPE['Observation'];

const PHASE_COLORS = ['#0052CC','#FF8B00','#DE350B','#FF8B00','#0C66E4'];
const PRIORITY_ICONS = ['🔐','📋','✅','📅','⚖️','🛑','🔍','🎯','⚠️','🛡️'];

const SVG = {
  health:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>`,
  signals:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
  time:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  discovery_rules:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  paths:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 8 8 3 13 8"/><polyline points="3 16 8 21 13 16"/><line x1="8" y1="3" x2="8" y2="21"/><polyline points="21 12 16 12"/></svg>`,
  priorities:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  note:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  evidence:`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`,
};
const secHdr = (svgKey,title,accent) =>
  `<div style="display:flex;align-items:center;gap:10px;margin-bottom:18px;"><div style="width:34px;height:34px;border-radius:8px;background:${accent};display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;">${SVG[svgKey]}</div><div style="font-size:17px;font-weight:700;color:#172B4D;">${esc(title)}</div></div>`;

const CSS = `
  html,body{margin:0;padding:0;box-sizing:border-box;}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#F4F5F7;color:#172B4D;padding:0;}
  .ea-wrap{display:flex;flex-direction:column;gap:0;padding:10px;}
  .ea-doc-header{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:14px 20px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center;}
  .ea-doc-header-left{display:flex;flex-direction:column;gap:4px;}
  .ea-doc-header-title{font-size:16px;font-weight:700;color:#172B4D;}
  .ea-doc-header-type{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#0052CC;}
  .ea-doc-header-time{font-size:12px;color:#6B778C;}
  .ea-section{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:18px 20px;margin-bottom:10px;}
  .ea-hero{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:20px 22px;position:relative;overflow:hidden;margin-bottom:10px;}
  .ea-hero::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#0052CC 0%,#FF8B00 50%,#00B8D9 100%);}
  .ea-hero-eyebrow{font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#0052CC;margin-bottom:10px;display:flex;align-items:center;gap:6px;}
  .ea-hero-eyebrow::before{content:'';display:inline-block;width:16px;height:2px;background:#0052CC;border-radius:2px;}
  .ea-hero-headline{font-size:22px;font-weight:800;line-height:1.25;color:#172B4D;margin-bottom:12px;}
  .ea-hero-summary{font-size:14px;line-height:1.7;color:#42526E;padding-bottom:16px;border-bottom:1px solid #EBECF0;margin-bottom:14px;}
  .ea-hero-bottom{margin-top:0;}
  .ea-score-banner{border-radius:10px;overflow:hidden;margin-top:16px;}
  .ea-score-banner-top{display:flex;align-items:center;gap:16px;padding:16px 20px;}
  .ea-score-ring-svg{flex-shrink:0;filter:drop-shadow(0 2px 8px rgba(9,30,66,.15));}
  .ea-score-meta{display:flex;flex-direction:column;gap:6px;flex:1;}
  .ea-score-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;opacity:0.75;margin-bottom:2px;}
  .ea-score-rating{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:6px;font-size:13px;font-weight:700;width:fit-content;}
  .ea-score-conf{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#6B778C;}
  .ea-score-rationale{font-size:13px;line-height:1.6;color:#42526E;margin-top:2px;}
  .ea-signal-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px;}
  .ea-signal-card{background:#FAFBFC;border:1px solid #EBECF0;border-radius:10px;padding:12px 14px;display:flex;flex-direction:column;gap:4px;transition:box-shadow .15s;}
  .ea-signal-card:hover{box-shadow:0 3px 12px rgba(9,30,66,.1);}
  .ea-signal-top{display:flex;justify-content:space-between;align-items:flex-start;gap:6px;margin-bottom:2px;}
  .ea-signal-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;}
  .ea-signal-value{font-size:22px;font-weight:800;color:#172B4D;line-height:1.15;}
  .ea-signal-why{font-size:12px;line-height:1.5;color:#42526E;}
  .ea-signal-status{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:2px 8px;border-radius:999px;border:1px solid #DFE1E6;color:#6B778C;background:#F4F5F7;white-space:nowrap;}
  .ea-signal-card.excellent{border-color:#7EE2B8;background:#F3FCF7;} .ea-signal-card.excellent .ea-signal-value{color:#216E4E;} .ea-signal-card.excellent .ea-signal-status{border-color:#7EE2B8;color:#216E4E;background:#E3FCEF;}
  .ea-signal-card.good{border-color:#85B7EB;background:#F0F7FF;} .ea-signal-card.good .ea-signal-value{color:#0052CC;} .ea-signal-card.good .ea-signal-status{border-color:#85B7EB;color:#0052CC;background:#E6F1FB;}
  .ea-signal-card.neutral{border-color:#DFE1E6;background:#FAFBFC;} .ea-signal-card.neutral .ea-signal-value{color:#172B4D;} .ea-signal-card.neutral .ea-signal-status{border-color:#DFE1E6;color:#6B778C;background:#F4F5F7;}
  .ea-signal-card.warning{border-color:#FFE380;background:#FFFBEE;} .ea-signal-card.warning .ea-signal-value{color:#974F0C;} .ea-signal-card.warning .ea-signal-status{border-color:#FFE380;color:#974F0C;background:#FFF0B3;}
  .ea-signal-card.critical{border-color:#FF8F73;background:#FFF8F6;} .ea-signal-card.critical .ea-signal-value{color:#BF2600;} .ea-signal-card.critical .ea-signal-status{border-color:#FF8F73;color:#BF2600;background:#FFEBE6;}
  .ea-signal-card.danger{border-color:#FF8F73;background:#FFF8F6;} .ea-signal-card.danger .ea-signal-value{color:#BF2600;}
  .ea-signal-card.ok{border-color:#85B7EB;background:#F0F7FF;} .ea-signal-card.ok .ea-signal-value{color:#0052CC;}
  .ea-time-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;margin-bottom:12px;}
  .ea-time-card{background:#FAFBFC;border:1px solid #EBECF0;border-radius:8px;padding:10px 12px;}
  .ea-time-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:4px;}
  .ea-time-value{font-size:18px;font-weight:800;color:#172B4D;line-height:1.2;}
  .ea-time-note{font-size:11px;color:#42526E;margin-top:2px;}
  .ea-discoveryRule-card{border:1px solid #EBECF0;border-radius:10px;background:#fff;margin-bottom:10px;overflow:hidden;}
  .ea-discoveryRule-card:last-child{margin-bottom:0;}
  .ea-discoveryRule-bar{display:flex;align-items:stretch;border-bottom:1px solid #EBECF0;}
  .ea-discoveryRule-num{padding:14px 16px;display:flex;flex-direction:column;align-items:center;justify-content:center;min-width:52px;border-right:1px solid #EBECF0;background:#FAFBFC;}
  .ea-discoveryRule-num-lbl{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;}
  .ea-discoveryRule-num-val{font-size:22px;font-weight:800;color:#172B4D;line-height:1;margin-top:2px;}
  .ea-discoveryRule-title-block{flex:1;padding:12px 16px;}
  .ea-discoveryRule-title{font-size:15px;font-weight:700;color:#172B4D;margin-bottom:6px;}
  .ea-discoveryRule-badges{display:flex;flex-wrap:wrap;gap:5px;}
  .ea-discoveryRule-body{display:grid;grid-template-columns:1fr 1fr 1fr;}
  .ea-discoveryRule-cell{padding:12px 16px;border-right:1px solid #EBECF0;}
  .ea-discoveryRule-cell:last-child{border-right:none;}
  .ea-cell-lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .ea-cell-lbl.insight{color:#FF8B00;} .ea-cell-lbl.action{color:#0052CC;} .ea-cell-lbl.impact{color:#0052CC;}
  .ea-cell-txt{font-size:13px;line-height:1.6;color:#42526E;}
  .ea-path-card{border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:14px 16px;margin-bottom:8px;}
  .ea-path-card:last-child{margin-bottom:0;}
  .ea-path-header{display:flex;align-items:center;gap:10px;margin-bottom:8px;}
  .ea-path-pct{font-size:24px;font-weight:800;color:#172B4D;line-height:1;}
  .ea-path-label{font-size:12px;font-weight:700;color:#6B778C;}
  .ea-path-flow{font-size:12px;color:#42526E;font-family:ui-monospace,monospace;background:#F4F5F7;border-radius:6px;padding:6px 10px;margin-bottom:8px;}
  .ea-path-insight{font-size:13px;line-height:1.6;color:#42526E;}
  .ea-priority-card{border:1px solid #EBECF0;border-radius:12px;background:#FAFBFC;padding:16px 18px;margin-bottom:10px;display:flex;align-items:flex-start;gap:14px;}
  .ea-priority-card:last-child{margin-bottom:0;}
  .ea-priority-icon{font-size:20px;flex-shrink:0;margin-top:2px;}
  .ea-priority-body{flex:1;}
  .ea-priority-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:8px;}
  .ea-priority-title{font-size:15px;font-weight:700;color:#172B4D;margin-bottom:0;}
  .ea-priority-detail{font-size:13px;line-height:1.6;color:#42526E;}
  .ea-priority-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:12px;}
  .ea-priority-mini{border:1px solid #EBECF0;border-radius:8px;background:#fff;padding:10px 12px;}
  .ea-priority-mini-lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:4px;}
  .ea-priority-tags{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}
  .ea-chip{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;border:1px solid #DFE1E6;background:#fff;color:#42526E;}
  .ea-chip.ai{background:#E6F1FB;color:#0052CC;border-color:#85B7EB;}
  .ea-chip.auto{background:#DEEBFF;color:#0747A6;border-color:#B3D4FF;}
  .ea-chip.risk{background:#FFEBE6;color:#BF2600;border-color:#FFBDAD;}
  .ea-impact-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;}
  .ea-impact-card{border:1px solid #EBECF0;border-radius:12px;background:#FAFBFC;padding:14px 16px;}
  .ea-impact-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:8px;}
  .ea-impact-value{font-size:24px;font-weight:800;color:#172B4D;line-height:1.15;}
  .ea-impact-note{font-size:12px;line-height:1.55;color:#42526E;margin-top:6px;}
  .ea-dual-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  .ea-list-card{border:1px solid #EBECF0;border-radius:12px;background:#FAFBFC;padding:14px 16px;}
  .ea-list-item{padding:10px 0;border-bottom:1px solid #EBECF0;}
  .ea-list-item:last-child{border-bottom:none;padding-bottom:0;}
  .ea-list-title{font-size:14px;font-weight:700;color:#172B4D;margin-bottom:4px;}
  .ea-readiness-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-bottom:12px;}
  .ea-readiness-card{border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:12px 14px;}
  .ea-readiness-pill{display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;}
  .ea-note{background:linear-gradient(135deg,#F0F7FF 0%,#E6F1FB 100%);border:1px solid #B3D4FF;border-radius:10px;padding:16px 18px;display:flex;gap:12px;align-items:flex-start;}
  .ea-note-icon{width:32px;height:32px;border-radius:8px;background:#0052CC;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;}
  .ea-note-text{font-size:13px;line-height:1.7;color:#0747A6;}
  .ea-cov-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;}
  .ea-cov-on{display:inline-flex;align-items:center;gap:5px;padding:5px 12px;border-radius:999px;font-size:12px;font-weight:600;background:#E6F1FB;color:#0052CC;border:1px solid #85B7EB;}
  .ea-cov-off{display:inline-flex;align-items:center;gap:5px;padding:5px 12px;border-radius:999px;font-size:12px;font-weight:600;background:#F4F5F7;color:#6B778C;border:1px solid #DFE1E6;}
  @media print{body{background:#fff;}.ea-section,.ea-hero{break-inside:avoid;}}
`;

// Score ring SVG
const scoreRing = (score, color) => {
  const r=40, cx=50, cy=50, circ=2*Math.PI*r;
  const pct=Math.max(0,Math.min(10,score||0))/10;
  const dash=pct*circ, gap=circ-dash;
  return `<svg width="90" height="90" viewBox="0 0 100 100" class="ea-score-ring-svg">
    <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#EBECF0" stroke-width="10"/>
    <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${color}" stroke-width="10"
      stroke-dasharray="${dash.toFixed(2)} ${gap.toFixed(2)}"
      stroke-linecap="round" transform="rotate(-90 50 50)"/>
    <text x="50" y="46" text-anchor="middle" font-size="20" font-weight="800" fill="#172B4D">${score||0}</text>
    <text x="50" y="60" text-anchor="middle" font-size="10" fill="#6B778C">out of 10</text>
  </svg>`;
};

const renderDocHeader = (modelName, reportType = 'Executive Assessment') => {
  const timestamp = new Date().toLocaleString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric', 
    hour: '2-digit', minute: '2-digit', hour12: true 
  });
  return `
<div class="ea-doc-header">
  <div class="ea-doc-header-left">
    <div class="ea-doc-header-type">${esc(reportType)}</div>
    <div class="ea-doc-header-title">${esc(modelName || 'Process Model')}</div>
  </div>
  <div class="ea-doc-header-time">${timestamp}</div>
</div>`;
};

const renderHero = (summary, health) => {
  if (!summary) return '';
  const hm = getHealth(health?.rating || '');
  const cm = getConf(health?.confidence || '');
  return `
<div class="ea-hero">
  <div class="ea-hero-eyebrow">Process Health</div>
  <div class="ea-hero-headline">${esc(summary.headline||'')}</div>
  <div class="ea-hero-summary">${fmt(summary.summary||'')}</div>
  ${health ? `
  <div class="ea-score-banner" style="background:linear-gradient(135deg,${hm.ring}15 0%,${hm.ring}08 100%);border:1px solid ${hm.ring}30;">
    <div class="ea-score-banner-top">
      ${scoreRing(health?.score, hm.ring)}
      <div class="ea-score-meta">
        <div class="ea-score-label" style="color:${hm.color};">Process Health Score</div>
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <div class="ea-score-rating" style="background:${hm.ring}20;color:${hm.color};">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${hm.bar};"></span>
            ${esc(health?.rating||'')}
          </div>
          <span style="display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${hm.ring}15;color:${hm.color};border:1px solid ${hm.ring}30;">${cm.icon} Confidence: ${esc(health?.confidence||'')}</span>
        </div>
      </div>
    </div>
    ${health?.rationale ? `<div style="font-size:13px;line-height:1.65;color:${hm.color};padding:12px 20px;border-top:1px solid ${hm.ring}20;background:${hm.ring}06;">${fmt(health.rationale)}</div>` : ''}
  </div>
  ` : ''}
</div>`;
};

const renderSignals = (signals) => {
  if (!Array.isArray(signals) || !signals.length) return '';
  const cards = signals.map(s => {
    const status = normaliseSignalStatus(s);
    return `<div class="ea-signal-card ${status.cls}">
      <div class="ea-signal-top"><div class="ea-signal-label">${esc(s.label||'')}</div><div style="display:flex;gap:5px;flex-wrap:wrap;justify-content:flex-end;">${srcBadge(s.source)}<span class="ea-signal-status">${esc(status.label)}</span></div></div>
      <div class="ea-signal-value">${esc(s.value||'')}</div>
      <div class="ea-signal-why">${fmt(s.why_it_matters||'')}</div>
    </div>`;
  }).join('');
  return `<div class="ea-section">${secHdr('signals','Headline Signals','#0052CC')}<div class="ea-signal-grid">${cards}</div></div>`;
};

const renderTimeProfile = (tp) => {
  if (!tp || !tp.available) return '';
  const metrics = Array.isArray(tp.metrics) ? tp.metrics : [];
  const metricCards = metrics.map(m =>
    `<div class="ea-time-card">
      <div class="ea-time-label">${esc(m.label||'')}</div>
      <div class="ea-time-value">${esc(m.value||'')}</div>
      ${m.note ? `<div class="ea-time-note">${esc(m.note)}</div>` : ''}
    </div>`
  ).join('');
  return `<div class="ea-section">${secHdr('time','Time Profile','#00B8D9')}
    ${tp.summary ? `<div style="font-size:13px;line-height:1.7;color:#42526E;margin-bottom:14px;">${fmt(tp.summary)}</div>` : ''}
    ${metricCards ? `<div class="ea-time-grid">${metricCards}</div>` : ''}
  </div>`;
};

const roiData = (data) => data?.roi_summary || data?.roi_estimate || null;
const readinessTone = (v='Medium') => {
  const value = String(v||'Medium');
  if (value === 'High') return { bg:'#E6F1FB', color:'#0052CC', border:'#85B7EB' };
  if (value === 'Low') return { bg:'#FFEBE6', color:'#BF2600', border:'#FFBDAD' };
  return { bg:'#FFF0B3', color:'#974F0C', border:'#FFE380' };
};

const renderBusinessImpactSummary = (data) => {
  const roi = roiData(data);
  const health = data?.overall_health || {};
  const cards = [];
  if (roi?.available && (roi.total_estimated_cost_display || roi.total_cost)) {
    cards.push({ label:'Estimated inefficiency cost', value: roi.total_estimated_cost_display || money(roi.total_cost, roi.currency || 'USD'), note: roi.headline || 'Directional estimate based on current workflow inefficiencies.' });
  }
  if (roi?.available && roi?.estimated_savings_range?.display) {
    cards.push({ label:'Savings opportunity', value: roi.estimated_savings_range.display, note: 'Potential recoverable value if the highest-priority issues are addressed.' });
  }
  cards.push({ label:'Process health', value: `${health.score || '—'}/10`, note: health.rationale || 'Overall health score combining delay, rework, complexity, and quality signals.' });
  const priCount = Array.isArray(data?.leadership_priorities) ? data.leadership_priorities.length : 0;
  cards.push({ label:'Leadership focus areas', value: priCount ? String(priCount) : '—', note: 'Highest-priority interventions for leadership attention.' });
  if (!cards.length) return '';
  return `<div class="ea-section">${secHdr('health','Business Impact Summary','#0C66E4')}<div class="ea-impact-grid">${cards.map(c => `<div class="ea-impact-card"><div class="ea-impact-label">${esc(c.label)}</div><div class="ea-impact-value">${esc(c.value)}</div><div class="ea-impact-note">${fmt(c.note||'')}</div></div>`).join('')}</div></div>`;
};

const renderTopAiOpportunities = (items) => {
  if (!Array.isArray(items) || !items.length) return '';
  return `<div class="ea-list-card"><div style="font-size:15px;font-weight:700;color:#172B4D;margin-bottom:6px;">Top AI Opportunities</div>${items.map(item => `<div class="ea-list-item"><div class="ea-list-title">${esc(item.title || item.use_case || '')}</div><div style="font-size:12px;color:#42526E;line-height:1.6;">${fmt(item.expected_benefit || '')}</div><div class="ea-priority-tags" style="margin-top:8px;">${item.use_case ? `<span class="ea-chip ai">AI Use Case: ${esc(item.use_case)}</span>` : ''}${item.automation_pairing ? `<span class="ea-chip auto">${esc(item.automation_pairing)}</span>` : ''}</div></div>`).join('')}</div>`;
};

const renderKeyRisks = (items) => {
  if (!Array.isArray(items) || !items.length) return '';
  return `<div class="ea-list-card"><div style="font-size:15px;font-weight:700;color:#172B4D;margin-bottom:6px;">Key Risks if Delayed</div>${items.map(item => `<div class="ea-list-item"><div class="ea-list-title">${esc(item.title || '')}</div><div style="font-size:12px;color:#42526E;line-height:1.6;">${fmt(item.impact || '')}</div>${item.urgency ? `<div class="ea-priority-tags" style="margin-top:8px;"><span class="ea-chip risk">Urgency: ${esc(item.urgency)}</span></div>` : ''}</div>`).join('')}</div>`;
};

const renderExecutiveDecisionSupport = (data) => {
  const ai = renderTopAiOpportunities(data?.top_ai_opportunities);
  const risks = renderKeyRisks(data?.key_risks);
  if (!ai && !risks) return '';
  return `<div class="ea-section">${secHdr('priorities','Executive Decision Support','#FF8B00')}<div class="ea-dual-grid">${ai || ''}${risks || ''}</div></div>`;
};

const renderReadiness = (readiness, coverage) => {
  if (!readiness && !coverage) return '';
  const r = readiness || {};
  const entries = [
    ['AI Readiness', r.ai_readiness || 'Medium'],
    ['Automation Readiness', r.automation_readiness || 'Medium'],
    ['Data Readiness', r.data_readiness || (coverage?.field_usage === false ? 'Low' : 'Medium')],
  ];
  return `<div class="ea-section">${secHdr('evidence','Readiness & Constraints','#6B778C')}<div class="ea-readiness-grid">${entries.map(([label,value]) => { const t = readinessTone(value); return `<div class="ea-readiness-card"><div class="ea-impact-label">${esc(label)}</div><span class="ea-readiness-pill" style="background:${t.bg};color:${t.color};border:1px solid ${t.border};">${esc(value)}</span></div>`; }).join('')}</div>${r.summary ? `<div style="font-size:13px;line-height:1.7;color:#42526E;">${fmt(r.summary)}</div>` : ''}</div>`;
};

const renderDiscoveryRules = (discoveryRules) => {
  if (!Array.isArray(discoveryRules) || !discoveryRules.length) return '';
  const cards = discoveryRules.map((f,i) => {
    const ft = getFT(f.discovery_rule_type);
    return `<div class="ea-discoveryRule-card">
      <div class="ea-discoveryRule-bar" style="border-top:3px solid ${ft.accent};">
        <div class="ea-discoveryRule-num"><div class="ea-discoveryRule-num-lbl">Find</div><div class="ea-discoveryRule-num-val">${i+1}</div></div>
        <div class="ea-discoveryRule-title-block">
          <div class="ea-discoveryRule-title">${esc(f.title||f.discovery_rule||'')}</div>
          <div class="ea-discoveryRule-badges">
            <span style="display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:5px;font-size:11px;font-weight:700;background:${ft.bg};color:${ft.color};">${ft.icon} ${esc(f.discovery_rule_type||'')}</span>
          </div>
        </div>
      </div>
      <div class="ea-discoveryRule-body">
        <div class="ea-discoveryRule-cell"><div class="ea-cell-lbl">📊 Evidence</div><div class="ea-cell-txt">${fmt(f.evidence||f.signal||'')}</div></div>
        <div class="ea-discoveryRule-cell"><div class="ea-cell-lbl insight">🔎 Insight</div><div class="ea-cell-txt">${fmt(f.insight||f.interpretation||f.why_it_matters||'')}</div></div>
        <div class="ea-discoveryRule-cell"><div class="ea-cell-lbl impact">💼 Business Impact</div><div class="ea-cell-txt">${fmt(f.business_impact||f.impact||'')}</div></div>
      </div>
    </div>`;
  }).join('');
  return `<div class="ea-section">${secHdr('discovery_rules','Major DiscoveryRules','#DE350B')}${cards}</div>`;
};


const renderPathInsights = (pi) => {
  if (!pi) return '';
  const dominant = Array.isArray(pi.dominant_paths) ? pi.dominant_paths : [];
  const problem = Array.isArray(pi.problem_paths) ? pi.problem_paths : (Array.isArray(pi.rework_patterns) ? pi.rework_patterns : []);
  const summary = pi.summary || pi.fragmentation_summary || pi.long_tail_note || '';
  const cards = [...dominant.map(p => ({ ...p, _group: 'Dominant Path' })), ...problem.map(p => ({ ...p, _group: 'Problem Path' }))].map(p =>
    `<div class="ea-path-card">
      <div class="ea-path-header">
        ${p.pct_of_items != null ? `<div class="ea-path-pct">${typeof p.pct_of_items === 'number' ? p.pct_of_items.toFixed(1)+'%' : esc(p.pct_of_items)}</div>` : ''}
        <div class="ea-path-label">${esc(p.path_label||p.variant_label||p.label||p.path_name||'')}</div>
        ${p._group ? `<span style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;background:#E6F1FB;color:#0052CC;">${esc(p._group)}</span>` : ''}
      </div>
      ${p.path_flow ? `<div class="ea-path-flow">${esc(p.path_flow)}</div>` : ''}
      <div class="ea-path-insight">${fmt(p.insight||p.why_notable||p.summary||'')}</div>
      ${Array.isArray(p.signals) && p.signals.length ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;">${p.signals.map(s => `<span style="display:inline-flex;align-items:center;padding:4px 8px;border-radius:999px;background:#F4F5F7;color:#42526E;font-size:11px;font-weight:600;">${esc(s)}</span>`).join('')}</div>` : ''}
    </div>`
  ).join('');
  if (!cards && !summary) return `<div class="ea-section">${secHdr('paths','Path Insights','#FF8B00')}<div style="border:1px dashed #C1C7D0;border-radius:10px;background:#FAFBFC;padding:14px 16px;color:#6B778C;font-size:13px;">No path insights were returned for this model.</div></div>`;
  return `<div class="ea-section">${secHdr('paths','Path Insights','#FF8B00')}
    ${summary ? `<div style="font-size:13px;line-height:1.7;color:#42526E;margin-bottom:12px;">${fmt(summary)}</div>` : ''}
    ${cards}
  </div>`;
};


const renderPriorities = (priorities) => {
  if (!Array.isArray(priorities) || !priorities.length) return '';
  const cards = priorities.map((p,i) => {
    const title = p.priority || p.title || '';
    const whyNow = p.why_now || p.rationale || p.detail || p.description || '';
    const benefit = p.expected_benefit || p.business_impact || '';
    const owner = p.owner_hint || '';
    const ai = p.ai_leverage || '';
    const automation = p.automation_path || '';
    const risk = p.risk_if_delayed || '';
    const type = p.priority_type || '';
    return `<div class="ea-priority-card">
      <div class="ea-priority-icon">${PRIORITY_ICONS[i%PRIORITY_ICONS.length]}</div>
      <div class="ea-priority-body">
        <div class="ea-priority-top">
          <div class="ea-priority-title">${esc(title)}</div>
          ${type ? `<span class="ea-chip">${esc(type)}</span>` : ''}
        </div>
        <div class="ea-priority-detail">${fmt(whyNow)}</div>
        <div class="ea-priority-grid">
          <div class="ea-priority-mini"><div class="ea-priority-mini-lbl">Expected Benefit</div><div class="ea-priority-detail">${fmt(benefit)}</div></div>
          <div class="ea-priority-mini"><div class="ea-priority-mini-lbl">Likely Owner</div><div class="ea-priority-detail">${fmt(owner)}</div></div>
        </div>
        ${(ai || automation || risk) ? `<div class="ea-priority-tags">${ai ? `<span class="ea-chip ai">AI: ${esc(ai)}</span>` : ''}${automation ? `<span class="ea-chip auto">Automation: ${esc(automation)}</span>` : ''}${risk ? `<span class="ea-chip risk">Risk if delayed: ${esc(risk)}</span>` : ''}</div>` : ''}
      </div>
    </div>`;
  }).join('');
  return `<div class="ea-section">${secHdr('priorities','Leadership Priorities','#FF8B00')}${cards}</div>`;
};

const renderNote = (note) => {
  if (!note) return '';
  return `<div class="ea-section">${secHdr('note','Consultant Note','#FF8B00')}<div class="ea-note"><div class="ea-note-icon">${SVG.note}</div><div class="ea-note-text">${fmt(note)}</div></div></div>`;
};

const renderEvidence = (cov) => {
  if (!cov) return '';
  const LABELS = {process_metrics:'Process Metrics',transitions:'Transitions',variants:'Variants',field_usage:'Field Usage',status_types:'Status Types',time_intelligence:'Time Intelligence',sample_items:'Sample Items'};
  const pills = Object.entries(LABELS).map(([k,label]) => {
    const on = cov[k] === true;
    return `<span class="ea-cov-${on?'on':'off'}">${on?'✓':'○'} ${label}</span>`;
  }).join('');
  const src = cov.source_summary || '';
  const sampleNote = cov.sample_item_count ? `<div style="font-size:12px;color:#6B778C;margin-top:8px;">Sample evidence: ${cov.sample_item_count} items</div>` : '';
  return `<div class="ea-section">${secHdr('evidence','Evidence Base','#6B778C')}${src?`<div style="font-size:13px;color:#42526E;margin-bottom:4px;">${esc(src)}</div>`:''}<div class="ea-cov-row">${pills}</div>${sampleNote}</div>`;
};

export const renderProcessHealthHTML = (data, modelTitle='') => {
  if (!data || typeof data !== 'object') return `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:24px">No data.<script id=\"__qa_data__\" type=\"application/json\">${JSON.stringify(data)}</script></body></html>`;
  const roiEnabled = data?.roi_enabled === true;
  const body = [
    renderHero(data.executive_summary, data.overall_health),
    renderSignals(data.headline_signals),
    renderRoiSummary(roiData(data), roiEnabled),
    renderRoiExplainer(roiData(data), roiEnabled),
    renderTimeProfile(data.time_profile),
    renderDiscoveryRules(data.major_discovery_rules),
    renderPathInsights(data.path_insights),
    renderPriorities(data.leadership_priorities),
    renderExecutiveDecisionSupport(data),
    renderReadiness(data.readiness_summary, data.data_coverage),
    renderNote(data.consultant_note),
    renderEvidence(data.data_coverage),
  ].filter(Boolean).join('\n');
  return `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="utf-8"/>\n<meta name="renderer-version" content="v2026-03-25-exec-upgrade"/>\n<meta name="viewport" content="width=device-width,initial-scale=1"/>\n<title>Process Health${modelTitle?' \u2014 '+modelTitle:''}</title>\n<style>${CSS}</style>\n</head>\n<body><div class="ea-wrap">\n${body}\n</div></body>\n</html>`;
};

export const buildProcessHealthExportHTML = (data, modelTitle='') => {
  if (!data || typeof data !== 'object') return `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:24px">No data.<script id=\"__qa_data__\" type=\"application/json\">${JSON.stringify(data)}</script></body></html>`;
  const roiEnabled = data?.roi_enabled === true;
  const body = [
    renderDocHeader(modelTitle, 'Executive Assessment'),
    renderHero(data.executive_summary, data.overall_health),
    renderSignals(data.headline_signals),
    renderRoiSummary(roiData(data), roiEnabled),
    renderRoiExplainer(roiData(data), roiEnabled),
    renderTimeProfile(data.time_profile),
    renderDiscoveryRules(data.major_discovery_rules),
    renderPathInsights(data.path_insights),
    renderPriorities(data.leadership_priorities),
    renderExecutiveDecisionSupport(data),
    renderReadiness(data.readiness_summary, data.data_coverage),
    renderNote(data.consultant_note),
    renderEvidence(data.data_coverage),
  ].filter(Boolean).join('\n');
  return `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="utf-8"/>\n<meta name="renderer-version" content="v2026-03-25-exec-upgrade"/>\n<meta name="viewport" content="width=device-width,initial-scale=1"/>\n<title>Process Health${modelTitle?' \u2014 '+modelTitle:''}</title>\n<style>${CSS}</style>\n</head>\n<body><div class="ea-wrap">\n${body}\n</div></body>\n</html>`;
};

export const renderProcessHealthHtml = renderProcessHealthHTML;